import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  CalendarCheck,
  CalendarClock,
  Check,
  Focus,
  Hand,
  Languages,
  Maximize2,
  MessageCircleHeart,
  Minimize2,
  Palette,
  Pause,
  Play,
  Type,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { shadeColour } from '@/lib/shades';
import {
  localeFor,
  PATIENT_LANGUAGES,
  patientStrings,
  resolveLanguage,
  type PatientLanguage,
} from '@/i18n/patient';
import { Logo } from '@/shared/ui/logo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { TooltipProvider } from '@/shared/ui/tooltip';
import type { AppointmentStatus, PatientShare, SceneManifest } from '@/types';
import type { ReviewViewerHandle } from '@/components/features/viewer/ReviewViewer';
import type { ViewPreset } from '@/components/features/viewer/engine/types';
import { FloatingPanel, StageButton } from '@/components/features/viewer/overlays/FloatingPanel';
import { ARButton } from './ARButton';

/**
 * What a patient sees: their new crown, with the tools to look at it and none
 * of the tools to judge it.
 *
 * So there is no section, no heatmap, no measurement, no parameters and no lab
 * or clinic detail - those are for deciding whether the crown is right, and
 * that is the dentist's job. What stays is everything that helps someone who
 * is not a clinician: plain-language views in their own language, a few
 * switches for what is showing, a slow spin, the crown in its real shade, a
 * before-and-after when there is a scan from before, the crown standing on
 * their table through the phone camera, and what their dentist told them -
 * the message and the fitting date to confirm.
 */
const ReviewViewer = lazy(() =>
  import('@/components/features/viewer/ReviewViewer').then((module) => ({
    default: module.ReviewViewer,
  })),
);

interface PatientWorkspaceProps {
  manifest: SceneManifest;
  share: PatientShare;
  /** The patient answers the proposed fitting date. */
  onAppointment?: (response: Exclude<AppointmentStatus, 'pending'>) => Promise<void>;
}

type Shown = { crowns: boolean; arch: boolean; opposite: boolean };

function isUpper(tooth: string | undefined): boolean {
  return Boolean(tooth && (tooth.startsWith('1') || tooth.startsWith('2')));
}

/** A switch a patient understands: a word, and on or off. No percentages. */
function Toggle({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-[13px] font-medium text-neutral-700 dark:text-neutral-200">
      {label}
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => {
          onChange(!on);
        }}
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          on ? 'bg-primary' : 'bg-neutral-300 dark:bg-neutral-600',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform',
            on && 'translate-x-4',
          )}
        />
      </button>
    </div>
  );
}

function Card({
  icon,
  title,
  children,
  className,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('patient-ui rounded-xl border border-border bg-card p-4', className)}>
      <h2 className="mb-2 flex items-center gap-2 text-[12px] font-semibold tracking-wide text-primary uppercase">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

export function PatientWorkspace({ manifest, share, onAppointment }: PatientWorkspaceProps) {
  const viewerRef = useRef<ReviewViewerHandle | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const [language, setLanguage] = useState<PatientLanguage>(() => resolveLanguage(share.language));
  const t = patientStrings(language);
  const [large, setLarge] = useState(false);

  const crowns = manifest.layers.filter((layer) => layer.kind === 'crown');
  const upperCase = crowns.some((layer) => isUpper(layer.tooth));
  const arch = upperCase ? 'upper' : 'lower';
  const opposite = upperCase ? 'lower' : 'upper';
  const teeth = crowns
    .map((layer) => layer.tooth)
    .filter((tooth): tooth is string => Boolean(tooth));

  // The crowns, their jaw, the opposite jaw and any scan from before
  // treatment. The bite scan, the previous version and the margin line are
  // clinical working material and stay out of the scene entirely.
  const patientManifest = useMemo<SceneManifest>(
    () => ({
      ...manifest,
      layers: manifest.layers.filter(
        (layer) =>
          layer.kind === 'crown' ||
          layer.kind === arch ||
          layer.kind === opposite ||
          layer.kind === 'preop',
      ),
      margins: [],
    }),
    [manifest, arch, opposite],
  );
  const hasBefore = patientManifest.layers.some((layer) => layer.kind === 'preop');

  const [shown, setShown] = useState<Shown>({ crowns: true, arch: true, opposite: false });
  /** 0 is before treatment, 1 is with the new crowns. */
  const [progress, setProgress] = useState(1);
  const [spinning, setSpinning] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const [appointment, setAppointment] = useState(share.appointment);
  const [answering, setAnswering] = useState(false);

  const opacity = useMemo(
    () =>
      Object.fromEntries(
        patientManifest.layers.map((layer) => {
          if (layer.kind === 'preop') return [layer.id, 1 - progress];
          if (layer.kind === 'crown') return [layer.id, shown.crowns ? progress : 0];
          if (layer.kind === arch) return [layer.id, shown.arch ? progress : 0];
          // The opposite jaw is shown see-through: solid, it hides the crown it bites on.
          return [layer.id, shown.opposite ? 0.45 : 0];
        }),
      ),
    [patientManifest, arch, shown, progress],
  );

  // Each crown in its prescribed shade.
  const crownShades = useMemo(
    () => Object.fromEntries(share.teeth.map((tooth) => [tooth.tooth, shadeColour(tooth.shade)])),
    [share.teeth],
  );
  const shades = [...new Set(share.teeth.map((tooth) => tooth.shade).filter(Boolean))].join(', ');

  const views: [ViewPreset, string][] = [
    ['iso', t.overview],
    ['front', t.front],
    ['left', t.leftSide],
    ['right', t.rightSide],
    // Looks down on a lower crown and up at an upper one.
    [upperCase ? 'bottom' : 'top', t.bitingSurface],
  ];

  useEffect(() => {
    const onChange = () => {
      setFullscreen(document.fullscreenElement === stageRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);

  // The page's language, for screen readers and for the browser's own font
  // choice for each script.
  useEffect(() => {
    document.documentElement.lang = localeFor(language);
  }, [language]);

  const chooseLanguage = (next: PatientLanguage) => {
    setLanguage(next);
    // Kept in the address, not in storage: reloading or sharing the page keeps
    // the language, and this portal stores nothing in the browser.
    const url = new URL(window.location.href);
    url.searchParams.set('lang', next);
    window.history.replaceState(null, '', url);
  };

  const answer = async (response: Exclude<AppointmentStatus, 'pending'>) => {
    if (!appointment) return;
    setAnswering(true);
    try {
      await onAppointment?.(response);
      setAppointment({ ...appointment, status: response });
    } finally {
      setAnswering(false);
    }
  };

  const showControls = (
    <>
      <p className="mb-0.5 text-[11px] font-semibold tracking-wide text-neutral-500 uppercase">
        {t.show}
      </p>
      <Toggle
        label={crowns.length > 1 ? t.newCrowns : t.newCrown}
        on={shown.crowns}
        onChange={(on) => {
          setShown((was) => ({ ...was, crowns: on }));
        }}
      />
      <Toggle
        label={t.yourTeeth}
        on={shown.arch}
        onChange={(on) => {
          setShown((was) => ({ ...was, arch: on }));
        }}
      />
      {patientManifest.layers.some((layer) => layer.kind === opposite) && (
        <Toggle
          label={upperCase ? t.lowerTeeth : t.upperTeeth}
          on={shown.opposite}
          onChange={(on) => {
            setShown((was) => ({ ...was, opposite: on }));
          }}
        />
      )}
    </>
  );

  const appointmentDate = appointment
    ? new Date(appointment.at).toLocaleString(localeFor(language), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  const sideCards = (
    <>
      {appointment && (
        <Card icon={<CalendarClock className="size-4" />} title={t.appointmentTitle}>
          <p className="text-[15px] font-semibold text-foreground">{appointmentDate}</p>
          {appointment.status === 'pending' ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={answering}
                onClick={() => void answer('confirmed')}
                className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-[13px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                <Check className="size-4" />
                {t.confirm}
              </button>
              <button
                type="button"
                disabled={answering}
                onClick={() => void answer('reschedule_requested')}
                className="rounded-lg border border-border px-3.5 py-2 text-[13px] font-medium hover:bg-muted disabled:opacity-60"
              >
                {t.anotherTime}
              </button>
            </div>
          ) : appointment.status === 'confirmed' ? (
            <p className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-success-600">
              <CalendarCheck className="size-4" />
              {t.confirmed}
            </p>
          ) : (
            <p className="mt-2 text-[13px] text-muted-foreground">{t.anotherTimeSent}</p>
          )}
        </Card>
      )}

      {share.message && (
        <Card icon={<MessageCircleHeart className="size-4" />} title={t.fromDentist}>
          <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{share.message.text}</p>
          <p className="mt-2 text-[12.5px] text-muted-foreground">{share.message.from}</p>
        </Card>
      )}

      {shades && (
        <p className="patient-ui flex gap-2 px-1 text-[12px] leading-relaxed text-muted-foreground">
          <Palette className="mt-0.5 size-3.5 shrink-0" />
          {t.shadeNote(shades)}
        </p>
      )}
    </>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          'flex min-h-dvh flex-col bg-background text-foreground md:h-dvh',
          large && 'patient-large',
        )}
      >
        <header className="patient-ui flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
          <Logo />
          <span className="h-6 w-px bg-border" />
          <h1 className="text-[15px] font-semibold">
            {crowns.length > 1 ? t.titleMany : t.titleOne}
          </h1>
          {teeth.length > 0 && (
            <span className="rounded-full bg-muted px-2.5 py-1 font-mono text-[12px] text-muted-foreground">
              {teeth.length > 1 ? t.teeth : t.tooth} {teeth.join(' · ')}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {/*
              Not a native <select>: its option list is drawn by the browser,
              light on a dark page in some of them and white-on-white in
              others. This one is drawn in the page's own colours.
            */}
            <Select
              value={language}
              onValueChange={(value) => {
                chooseLanguage(value as PatientLanguage);
              }}
            >
              <SelectTrigger
                size="sm"
                aria-label={t.language}
                className="gap-1.5 rounded-lg border-border px-2 text-[13px] font-medium shadow-none"
              >
                <Languages className="size-4 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" align="end">
                {PATIENT_LANGUAGES.map((item) => (
                  <SelectItem key={item.code} value={item.code} className="text-[13px]">
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              aria-pressed={large}
              onClick={() => {
                setLarge((was) => !was);
              }}
              title={t.largerText}
              className={cn(
                'flex items-center gap-1 rounded-lg border px-2 py-1.5 text-[13px] font-medium',
                large ? 'border-primary bg-primary/10 text-primary' : 'border-border',
              )}
            >
              <Type className="size-4" />
              <span className="hidden sm:inline">{t.largerText}</span>
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row md:gap-3 md:px-3 md:pb-3">
          <main
            ref={stageRef}
            className="stage-ground relative h-[70vh] shrink-0 overflow-hidden sm:mx-3 sm:rounded-2xl md:mx-0 md:h-auto md:flex-1"
          >
            <Suspense fallback={null}>
              <ReviewViewer
                ref={viewerRef}
                manifest={patientManifest}
                opacity={opacity}
                showMargins={false}
                autoRotate={spinning}
                crownShades={crownShades}
                className="absolute inset-0"
              />
            </Suspense>

            <div className="pointer-events-none absolute inset-0 *:pointer-events-auto">
              {/* Views, and which tooth to look at */}
              <div className="patient-ui absolute top-3 left-3 flex flex-col items-start gap-2">
                <FloatingPanel role="group" className="flex flex-wrap gap-0.5 p-1">
                  {views.map(([preset, label]) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => {
                        setSpinning(false);
                        if (preset === 'iso') viewerRef.current?.frameCrowns();
                        else viewerRef.current?.setView(preset);
                      }}
                      className="h-8 rounded-md px-3 text-[12.5px] font-medium text-neutral-600 transition-colors hover:bg-neutral-900/5 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      {label}
                    </button>
                  ))}
                </FloatingPanel>
                {teeth.length > 1 && (
                  <FloatingPanel role="group" className="flex items-center gap-0.5 p-1">
                    {[...teeth, null].map((tooth) => (
                      <button
                        key={tooth ?? 'all'}
                        type="button"
                        aria-pressed={focused === tooth}
                        onClick={() => {
                          setFocused(tooth);
                          if (tooth) viewerRef.current?.focusTooth(tooth);
                          else viewerRef.current?.frameCrowns();
                        }}
                        className={cn(
                          'h-7 rounded-md px-2.5 text-[12px] font-semibold transition-colors',
                          focused === tooth
                            ? 'bg-primary text-primary-foreground'
                            : 'text-neutral-600 hover:bg-neutral-900/5 dark:text-neutral-300 dark:hover:bg-white/10',
                        )}
                      >
                        {tooth ? `${t.tooth} ${tooth}` : t.both}
                      </button>
                    ))}
                  </FloatingPanel>
                )}
              </div>

              {/* What is showing. Below the model on a phone, where it would cover the views. */}
              <FloatingPanel className="patient-ui absolute top-3 right-3 hidden w-56 px-3 py-2 md:block">
                {showControls}
              </FloatingPanel>

              {/* Before and after, when the lab has a scan from before treatment */}
              {hasBefore && (
                <FloatingPanel className="patient-ui absolute inset-x-3 bottom-32 mx-auto flex max-w-sm items-center gap-3 px-4 py-2.5 md:bottom-20">
                  <span className="text-[12.5px] font-semibold">{t.before}</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={Math.round(progress * 100)}
                    aria-label={`${t.before} / ${t.after}`}
                    onChange={(event) => {
                      setProgress(Number(event.target.value) / 100);
                    }}
                    className="stage-range flex-1"
                  />
                  <span className="text-[12.5px] font-semibold">{t.after}</span>
                </FloatingPanel>
              )}

              {/* Seeing it in the room, and the viewing tools */}
              <div className="patient-ui absolute inset-x-0 bottom-3 mx-auto flex w-fit flex-col items-center gap-2 md:right-3 md:left-auto md:mx-0 md:items-end">
                <ARButton
                  label={t.seeInRoom}
                  unsupported={t.arNotSupported}
                  getModel={() => viewerRef.current?.exportCrownsGlb() ?? Promise.resolve(null)}
                />
                <p className="flex items-center gap-2 rounded-full bg-white/80 px-3 py-1.5 text-[12px] text-neutral-600 shadow-sm backdrop-blur dark:bg-neutral-900/70 dark:text-neutral-300">
                  <Hand className="size-3.5" />
                  {t.hint}
                </p>
                <FloatingPanel role="toolbar" className="flex items-center gap-0.5 p-1">
                  <StageButton label={t.zoomOut} onClick={() => viewerRef.current?.zoom(1.33)}>
                    <ZoomOut />
                  </StageButton>
                  <StageButton label={t.zoomIn} onClick={() => viewerRef.current?.zoom(0.75)}>
                    <ZoomIn />
                  </StageButton>
                  <StageButton
                    label={spinning ? t.stopTurning : t.turnSlowly}
                    active={spinning}
                    onClick={() => {
                      setSpinning((was) => !was);
                    }}
                  >
                    {spinning ? <Pause /> : <Play />}
                  </StageButton>
                  <StageButton
                    label={t.backToStart}
                    onClick={() => {
                      setFocused(null);
                      viewerRef.current?.frameCrowns();
                    }}
                  >
                    <Focus />
                  </StageButton>
                  <StageButton
                    label={fullscreen ? t.exitFullScreen : t.fullScreen}
                    onClick={() => {
                      if (document.fullscreenElement) void document.exitFullscreen();
                      else void stageRef.current?.requestFullscreen();
                    }}
                  >
                    {fullscreen ? <Minimize2 /> : <Maximize2 />}
                  </StageButton>
                </FloatingPanel>
              </div>
            </div>
          </main>

          <aside className="flex flex-col gap-3 p-3 md:w-80 md:shrink-0 md:overflow-y-auto md:p-0">
            <FloatingPanel className="patient-ui px-3 py-2 md:hidden">{showControls}</FloatingPanel>
            {sideCards}
          </aside>
        </div>
      </div>
    </TooltipProvider>
  );
}
